import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Button } from 'reactstrap';
import { Icon } from 'js/components';
import { faArrowUp } from '@fortawesome/pro-solid-svg-icons';

import AskNext from './AskNext';
import { stripMarks } from './OrgBriefing';
import RegenerateBriefingIcon from './RegenerateBriefingIcon';
import styles from './OrgAnalyze.module.scss';

function placeCaretAtEnd(node) {
  if (!node) {
    return;
  }
  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(node);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
}

export function promptList(value) {
  return (value ?? [])
    .map((item) => (typeof item === 'string' ? item : item?.question))
    .map((question) => stripMarks((question || '').trim()))
    .filter(Boolean)
    .slice(0, 3)
    .map((question) => ({ question }));
}

const ChatComposer = forwardRef(function ChatComposer(
  { disabled, sending, prompts, onSend, onRegenerate, regenerating },
  ref
) {
  const editorRef = useRef(null);
  const [hasText, setHasText] = useState(false);

  const readText = () =>
    (editorRef.current?.innerText ?? '').replace(/\u00a0/g, ' ');

  const clearEditor = () => {
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
    setHasText(false);
  };

  const appendPrompt = useCallback((text) => {
    if (!editorRef.current || !text) {
      return;
    }
    const current = (editorRef.current.innerText ?? '').replace(/\u00a0/g, ' ');
    const next =
      current.trim() === '' ? text : `${current.replace(/\s+$/, '')}\n${text}`;
    editorRef.current.innerText = next;
    setHasText(Boolean(next.trim()));
    editorRef.current.focus();
    placeCaretAtEnd(editorRef.current);
  }, []);

  useImperativeHandle(ref, () => ({ appendPrompt }), [appendPrompt]);

  const submit = () => {
    const content = readText().trim();
    if (!content || disabled) {
      return;
    }
    clearEditor();
    onSend(content);
  };

  const items = promptList(prompts);

  return (
    <div className={styles.dock}>
      <div className={styles.composer}>
        <div
          ref={editorRef}
          className={styles.editor}
          contentEditable={!disabled}
          role="textbox"
          aria-multiline="true"
          aria-label="Ask the analyst"
          data-placeholder="Ask the analyst"
          data-empty={hasText ? 'false' : 'true'}
          onInput={() => {
            const empty = !readText().trim();
            // Browsers leave a <br> (or a wrapping <div><br></div>) after the
            // last character is deleted, which would sit on its own line
            // under the placeholder. Collapse back to a truly empty node.
            if (empty && editorRef.current?.innerHTML) {
              editorRef.current.innerHTML = '';
            }
            setHasText(!empty);
          }}
          onPaste={(event) => {
            event.preventDefault();
            const text = event.clipboardData.getData('text/plain');
            document.execCommand('insertText', false, text);
            setHasText(Boolean(readText().trim()));
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
        />
        <div className={styles.composerActions}>
          <AskNext
            items={items}
            onPick={appendPrompt}
            disabled={disabled}
            variant="icon"
          />

          <button
            type="button"
            className={styles.actionIcon}
            aria-label="Regenerate the briefing"
            title="Regenerate the briefing"
            onClick={onRegenerate}
            disabled={disabled || sending || regenerating}
          >
            <RegenerateBriefingIcon spin={regenerating} />
          </button>

          <Button
            color="primary"
            className={styles.send}
            onClick={submit}
            disabled={disabled || sending || !hasText}
          >
            <Icon icon={faArrowUp} />
          </Button>
        </div>
      </div>
    </div>
  );
});

export default ChatComposer;
